import cx from "classnames";
import { LocationDescriptor } from "history";
import sortBy from "lodash-es/sortBy";
import React, { useMemo, useState } from "react";

import CategoryFacet from "@/mk2/apps/bazaar/components/CategoryFacet";
import { CategoryKey } from "@/mk2/apps/bazaar/components/CategoryFacetOption";
import CategoryFacetOptions, {
    CategoryOption,
} from "@/mk2/apps/bazaar/components/CategoryFacetOptions";
import { SubcategoryOption } from "@/mk2/apps/bazaar/components/CategoryFacetSubOptions";
import Facet from "@/mk2/apps/bazaar/components/Facet";
import { OptionKey } from "@/mk2/apps/bazaar/components/FacetDropdownOption";
import LocationFacet from "@/mk2/apps/bazaar/components/LocationFacet";
import PriceFacet from "@/mk2/apps/bazaar/components/PriceFacet";
import { PriceType } from "@/mk2/apps/bazaar/components/PriceFacetDropdown";
import { Modal } from "@/mk2/components/Modal";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import {
    Category,
    getParentCategory,
    getPropertiesForCategories,
    list,
} from "@/mk/bazaar/product/categories";

import styles from "./FacetsFilter.mscss";

export type FacetData = [OptionKey, string, number] | [OptionKey, number]; // [ key, label?, count ]

export const getFacetCount = (facetData: FacetData): number => {
    return facetData.length === 2 ? facetData[1] : facetData[2];
};

export interface FacetsData {
    [facetKey: string]: FacetData[];
}

interface SelectedFacets {
    [facetKey: string]: OptionKey[];
}

interface NormalizedFacetData {
    [facetKey: string]: Array<{ key: OptionKey; label: string; count: number }>;
}

export interface OwnProps {
    className?: string;
    isMobile: boolean;
    facets: FacetsData;
    category: CategoryKey;
    selectedFacets: SelectedFacets;
    price: {
        min: number;
        max: number;
    };

    openCategoriesIfEmpty?: boolean;
    placesRadius: boolean;

    generateCategoryTo?(category: CategoryKey): LocationDescriptor;
    onFacetClear(facetKey: string);
    onFacetChange(facetKey: string, optionKey: OptionKey, checked: boolean);
    onFacetToggle?(facetKey: string, opened: boolean);
    onCategoryClear();
    onCategoryChange(category: CategoryKey);
    onPriceClear();
    onPriceChange(type: PriceType, value: number);
    onPlacesRadiusChange(enabled: boolean);
}

type Props = OwnProps;

const pBrandAlwaysDisplayed = ["OTHER_BRAND"];
const pBrandModelAlwaysDisplayed = ["OTHER_BRAND_MODEL"];
const pManufacturerAlwaysDisplayed = ["OTHER_BRAND"];
const pFitsOnModelAlwaysDisplayed = ["OTHER_BRAND_MODEL", "UNIVERSAL_BRAND_MODEL"];

const FacetsFilter: React.FunctionComponent<Props> = ({
    className,
    isMobile,
    facets,
    category,
    price,
    selectedFacets,
    generateCategoryTo,
    onFacetChange,
    onFacetClear,
    onFacetToggle,
    onCategoryChange,
    onCategoryClear,
    onPriceChange,
    onPriceClear,
    openCategoriesIfEmpty = false,
    placesRadius,
    onPlacesRadiusChange,
}) => {
    const [opened, setOpened] = useState<string>(
        openCategoriesIfEmpty && !category ? "cats" : null,
    );
    const openedFacet = isMobile && opened ? null : opened;

    const [openedCategory, setOpenedCategory] = useState<CategoryKey>(null);

    const facetKeys = useMemo(() => {
        const propsForCategory = getPropertiesForCategories(category && [category]);
        const keys = Object.keys(facets).filter(
            (key) =>
                // Quality is manually displayed
                key !== "quality" &&
                // Category is also manually displayed
                key !== "cats" &&
                // Hide props not listed for given category
                propsForCategory.includes(key) &&
                // Hide if facet has no value
                facets[key].length,
        );

        // compute facets positions
        const facetsSortMap = propsForCategory.reduce<{ [prop: string]: number }>(
            (acc, cur, index) => {
                acc[cur] = index;
                return acc;
            },
            {},
        );

        return sortBy<string>(keys, (item) => {
            return facetsSortMap[item];
        });
    }, [facets, category]);

    const normalizedFacets = useMemo<NormalizedFacetData>(() => {
        return Object.keys(facets).reduce<NormalizedFacetData>((acc, cur) => {
            acc[cur] = facets[cur].map((data) => ({
                key: data[0],
                label: (data.length === 2 ? data[0] : data[1]).toString(),
                count: data.length === 2 ? data[1] : data[2],
            }));
            return acc;
        }, {});
    }, [facets, selectedFacets]);

    const categories = useMemo((): CategoryOption[] => {
        const counts = normalizedFacets.cats
            ? normalizedFacets.cats.reduce((acc, cur) => {
                  acc[cur.key] = cur.count;
                  return acc;
              }, {})
            : {};

        const categoryToSubOption = (cat: Category): SubcategoryOption => ({
            key: cat.name,
            to: "children" in cat ? null : generateCategoryTo?.(cat.name),
            count: counts[cat.name] || 0,
            children: "children" in cat ? cat.children?.map(categoryToSubOption) : null,
        });

        return list().map((cat) => ({
            key: cat.name,
            to: generateCategoryTo?.(cat.name),
            count: counts[cat.name] || 0,
            children: cat.children?.map(categoryToSubOption),
        }));
    }, [normalizedFacets]);

    const numberOfFacets =
        facetKeys.length + 2 + (facets.places?.length || facets.locations?.length ? 1 : 0);
    const size = numberOfFacets < 5 ? "big" : numberOfFacets === 7 ? "small" : "normal";

    const onToggleCategoryFacetCallback = useEventCallback(() => {
        setOpened((state) => (state === "cats" ? null : "cats"));
        setOpenedCategory(null);
        if (onFacetToggle) {
            onFacetToggle("cats", openedFacet !== "cats");
        }
    }, [setOpened, setOpenedCategory, onFacetToggle, openedFacet]);

    const onToggleCategoryWithChildrenCallback = useEventCallback(
        (cat: CategoryKey) => {
            setOpenedCategory((state) => (state === cat ? getParentCategory(cat)?.name : cat));
        },
        [setOpenedCategory],
    );

    const onSelectCategoryCallback = useEventCallback(
        (cat: CategoryKey) => {
            onCategoryChange(cat);
            setOpened(null); // Close after category select
            setOpenedCategory(null);
        },
        [setOpenedCategory, onCategoryChange],
    );

    const onClearCategoryFacetCallback = useEventCallback(() => {
        onCategoryClear();
        setOpened(openCategoriesIfEmpty ? "cats" : null); // Close after category select
        setOpenedCategory(null);
    }, [setOpenedCategory, onCategoryChange]);

    const onTogglePriceFacetCallback = useEventCallback(() => {
        setOpened((state) => (state === "price" ? null : "price"));
        if (onFacetToggle) {
            onFacetToggle("price", openedFacet !== "price");
        }
    }, [setOpened, onFacetToggle, openedFacet]);

    const onToggleFacetCallback = useEventCallback(
        (facetKey: string) => {
            setOpened((state) => (state === facetKey ? null : facetKey));
            if (onFacetToggle) {
                onFacetToggle(facetKey, openedFacet !== facetKey);
            }
        },
        [setOpened, onFacetToggle, openedFacet],
    );

    return (
        <div className={cx(styles.FacetsFilter, className, styles[`FacetsFilter--${size}`])}>
            <div className={styles.FacetsFilter__filters}>
                <CategoryFacet
                    className={cx(
                        styles.FacetsFilter__facet,
                        styles[`FacetsFilter__facet--${size}`],
                    )}
                    selected={category}
                    dimmed={openedFacet && openedFacet !== "cats"}
                    onToggle={onToggleCategoryFacetCallback}
                    onClear={onClearCategoryFacetCallback}
                />
                <PriceFacet
                    className={cx(
                        styles.FacetsFilter__facet,
                        styles[`FacetsFilter__facet--${size}`],
                    )}
                    isMobile={isMobile}
                    onToggle={onTogglePriceFacetCallback}
                    onClear={onPriceClear}
                    onChange={onPriceChange}
                    opened={openedFacet === "price"}
                    dimmed={openedFacet && openedFacet !== "price"}
                    min={price.min}
                    max={price.max}
                />
                {Boolean(facets.places?.length || facets.locations?.length) && (
                    <LocationFacet
                        className={cx(
                            styles.FacetsFilter__facet,
                            styles[`FacetsFilter__facet--${size}`],
                        )}
                        isMobile={isMobile}
                        locationsOptions={normalizedFacets.locations}
                        placesOptions={normalizedFacets.places}
                        selectedLocations={selectedFacets.locations}
                        selectedPlaces={selectedFacets.places}
                        placesRadius={placesRadius}
                        onToggle={onToggleFacetCallback}
                        opened={openedFacet === "locations"}
                        dimmed={openedFacet && openedFacet !== "locations"}
                        onChange={onFacetChange}
                        onClear={onFacetClear}
                        onPlacesRadiusChange={onPlacesRadiusChange}
                    />
                )}
                {facetKeys.map((key) => (
                    <Facet
                        className={cx(
                            styles.FacetsFilter__facet,
                            styles[`FacetsFilter__facet--${size}`],
                            styles[`FacetsFilter__facet--${key}`],
                        )}
                        isMobile={isMobile}
                        key={key}
                        alwaysDisplayed={
                            key === "p_brand"
                                ? pBrandAlwaysDisplayed
                                : key === "p_brand_model" || key === "p_brand_models"
                                  ? pBrandModelAlwaysDisplayed
                                  : key === "p_manufacturer"
                                    ? pManufacturerAlwaysDisplayed
                                    : key === "p_fits_on_model"
                                      ? pFitsOnModelAlwaysDisplayed
                                      : null
                        }
                        facetKey={key}
                        options={normalizedFacets[key]}
                        multiselect
                        isFilter
                        canClear
                        search
                        opened={openedFacet === key}
                        selected={selectedFacets[key]}
                        onToggle={onToggleFacetCallback}
                        dimmed={openedFacet && openedFacet !== key}
                        onChange={onFacetChange}
                        onClear={onFacetClear}
                    />
                ))}
            </div>

            {isMobile ? ( // this should fix iphone safari bug with invisible header
                <Modal
                    className={cx(
                        styles.FacetsFilter__categories,
                        styles[`FacetsFilter__categories--${size}`],
                    )}
                    show={openedFacet === "cats"}
                    backdrop={false}
                >
                    <CategoryFacetOptions
                        options={categories}
                        opened={openedCategory}
                        selected={category}
                        onToggle={onToggleCategoryWithChildrenCallback}
                        onSelect={onSelectCategoryCallback}
                        onClose={onToggleCategoryFacetCallback}
                    />
                </Modal>
            ) : openedFacet === "cats" ? (
                <div
                    className={cx(
                        styles.FacetsFilter__categories,
                        styles[`FacetsFilter__categories--${size}`],
                    )}
                >
                    {numberOfFacets < 7 && (
                        <span
                            className={cx(
                                styles.FacetsFilter__categories__triangle,
                                styles[`FacetsFilter__categories__triangle--${size}`],
                            )}
                        />
                    )}
                    <CategoryFacetOptions
                        options={categories}
                        opened={openedCategory}
                        selected={category}
                        onToggle={onToggleCategoryWithChildrenCallback}
                        onSelect={onSelectCategoryCallback}
                        onClose={onToggleCategoryFacetCallback}
                    />
                </div>
            ) : null}
        </div>
    );
};

FacetsFilter.displayName = "FacetsFilter";

export default React.memo<Props>(FacetsFilter);
